import { HelpIcon, EndpointHint } from '../../core/index.js'
import type { DraftAssignment, WorkerBoardView, WorkerRunResult } from '../types.js'
import { describeDay, describePeriod, PRIORITY_LABELS, SHIFT_TYPE_LABELS } from './preferencesText.js'
import { describeAwarded, formatRunInstant } from './scheduleRunText.js'
import { DayCornerMark } from './ScheduleCalendar.js'
import { ORIGIN_LABEL, REMOVED_LABEL } from './scheduleDayMarks.js'

/** The month before `period`, `YYYY-MM`. */
function monthBefore(period: string): string {
  const [year, month] = period.split('-').map(Number)
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 2, 1)).toISOString().slice(0, 7)
}

/** One released month as the worker's calendar shows it: the result as released and the board as it stands now. */
export interface ReleasedMonthData {
  period: string
  result: WorkerRunResult
  releasedAt: string | null
  /** The board as the worker reads it; null until it is read, when the month reads as released. */
  board: WorkerBoardView | null
}

function changeCount(board: WorkerBoardView | null): number {
  return (board?.origins?.length ?? 0) + (board?.removed?.length ?? 0)
}

/** How the month was built, in a sentence, for the cases where that needs saying. */
function builtFrom(result: WorkerRunResult, period: string): string {
  if (result.preferencesSource === 'previous_month') {
    return `You didn’t save preferences for ${describePeriod(period)}, so your ${describePeriod(monthBefore(period))} ones were used: the same shift order and hours. PTO and days off were not carried over.`
  }
  if (result.preferencesSource === 'none') {
    return `You didn’t save preferences for ${describePeriod(period)}, so your month was built from the shifts left after everyone who did. You can swap shifts or pick up open ones below.`
  }
  if (result.outcome === 'no_preferences') {
    return 'You had not saved preferences for this month, so nothing was drafted for you. Speak to your scheduler.'
  }
  if (result.outcome === 'nothing_fit') {
    return 'No published shift fit your preferences once those ranked ahead had theirs. Speak to your scheduler.'
  }
  return result.outcome === 'assigned' ? 'Assigned by your facility’s rules for your tier.' : 'Built from your preferences, in rank order with everyone else’s.'
}

/**
 * The calendar's header for a released month (worker calendar design, 26 October 2026): the month as it stands now -
 * shifts and hours with every change since the release applied - when it was released, and how many changes there were.
 */
export function ReleasedMonthHeader({ data }: { data: ReleasedMonthData }) {
  const { result, period, releasedAt, board } = data
  const shifts: readonly DraftAssignment[] = board?.assignments ?? result.awarded
  const hours = board?.hours ?? result.hours
  const changes = changeCount(board)
  return (
    <div className="mb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            Your {describePeriod(period)} schedule
            <HelpIcon topic="schedule.worker-result" />
            <EndpointHint>GET /workers/{'{workerId}'}/schedule/result</EndpointHint>
          </div>
          <div className="text-xs font-extrabold text-ink">
            {describeAwarded(shifts, hours)}
            {releasedAt ? ` · released ${formatRunInstant(releasedAt)}` : ''}
          </div>
        </div>
        {changes > 0 && <span className="shrink-0 rounded-full bg-marine-lt px-2 py-0.5 text-[10px] font-extrabold text-marine-dk">{changes === 1 ? '1 change since release' : `${changes} changes since release`}</span>}
      </div>
      <p className="mt-0.5 text-[11px] leading-snug text-ink-mid">{builtFrom(result, period)}</p>
    </div>
  )
}

/** A short date with its weekday: "Mon Oct 26". */
function weekdayDate(date: string): string {
  const parsed = new Date(`${date}T12:00:00`)
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

/** What changed since the release, newest day last, then what the build decided: shifts lost, days off taken, PTO. */
export function ReleasedMonthNotes({ data, unitNames }: { data: ReleasedMonthData; unitNames: ReadonlyMap<string, string> }) {
  const { result, board } = data
  const heldByShift = new Map((board?.assignments ?? []).map((entry) => [entry.shiftId, entry]))
  const unitOf = (unitId: string) => unitNames.get(unitId) ?? unitId
  const awayFrom = (unitId: string) => (board?.homeUnitId && unitId !== board.homeUnitId ? `, ${unitOf(unitId)}` : '')
  const changes = [
    ...(board?.origins ?? []).map((origin) => {
      const shift = heldByShift.get(origin.shiftId)
      return { key: `o-${origin.shiftId}`, date: origin.date, kind: origin.kind, coral: false, text: `${shift ? `${SHIFT_TYPE_LABELS[shift.shiftType]}${awayFrom(shift.unitId)}` : ''} · ${ORIGIN_LABEL[origin.kind].toLowerCase()}` }
    }),
    ...(board?.removed ?? []).map((removal) => ({ key: `r-${removal.shiftId}`, date: removal.date, kind: 'removed' as const, coral: true, text: `${SHIFT_TYPE_LABELS[removal.shiftType]} · ${REMOVED_LABEL[removal.kind].toLowerCase()}` })),
  ].sort((a, b) => a.date.localeCompare(b.date))

  return (
    <div className="mt-3 flex flex-col gap-2">
      {changes.length > 0 && (
        <div>
          <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Since the release</div>
          <ul className="divide-y divide-marine/10">
            {changes.map((change) => (
              <li key={change.key} className="flex items-center gap-2.5 py-1.5 text-[11px] text-ink">
                <DayCornerMark kind={change.kind} coral={change.coral} className="relative shrink-0" />
                <span>
                  <strong>{weekdayDate(change.date)}</strong> {change.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {result.lost.length > 0 && (
        <p className="text-[11px] leading-snug text-ink">
          <span className="font-bold">
            {result.lost.length} {result.lost.length === 1 ? 'shift' : 'shifts'} you asked for went to someone ranked ahead:
          </span>{' '}
          {result.lost.map((loss) => `${describeDay(loss.date)} ${SHIFT_TYPE_LABELS[loss.shiftType].toLowerCase()}`).join(', ')}. Your reliability score is your rank.
        </p>
      )}
      {(result.takenDaysOff ?? []).length > 0 && (
        <p className="text-[11px] leading-snug text-ink">
          <span className="font-bold">A day off was taken to cover your unit:</span> {(result.takenDaysOff ?? []).map((entry) => `${describeDay(entry.date)} ${SHIFT_TYPE_LABELS[entry.shiftType].toLowerCase()} (your ${PRIORITY_LABELS[entry.priority]} day)`).join(', ')}. Low days go first, then Medium, then High, and the lowest-ranked bidder off that day covers it. A higher reliability score protects your days off.
        </p>
      )}
      {result.flags.includes('under_target') && <p className="text-[11px] text-coral">Under your target: not enough fit. Your scheduler sees this.</p>}
      {result.ptoDecisions.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-[11px] leading-snug" aria-label="PTO">
          {result.ptoDecisions.map((entry) => (
            <li key={entry.date} className={entry.status === 'denied' ? 'text-coral' : 'text-ink-mid'}>
              PTO {describeDay(entry.date)}: {entry.status === 'approved' ? 'approved' : entry.status === 'denied' ? (entry.reason === 'below_minimum' ? 'denied - your unit is short that day, so it is a working day' : 'denied - a working day') : 'requested; your facility decides'}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
